import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { LoveItAlertService } from "../../services/love-it-alert.service";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { Abwesenheit } from "../../../api/models/abwesenheit";
import { MailmanagerCreateSieveRequest } from "../../../api/models/mailmanager-create-sieve-request";
import { MailmanagerSieveFilter } from "../../../api/models/mailmanager-sieve-filter";
import { MailmanagerUpdateSieveRequest } from "../../../api/models/mailmanager-update-sieve-request";
import { MailmanagerService } from "../../../api/services/mailmanager.service";
import { AbwesenheitTyp, ToastType } from "../../enums/global-enums";
import { LoveItToastService } from "../../services/love-it-toast.service";

@Component({
  selector: "app-email-abwesenheitsbenachrichtigung",
  templateUrl: "./email-abwesenheitsbenachrichtigung.component.html",
  styleUrls: ["./email-abwesenheitsbenachrichtigung.component.scss"],
  standalone: false,
})
export class EmailAbwesenheitsbenachrichtigungComponent implements OnInit {
  @Input({ required: true })
  userId: string;
  @Input({ required: true })
  userData;
  @Input({ required: false })
  existingSieveFilter: MailmanagerSieveFilter;

  isSaving: boolean = false;
  submitted: boolean = false;

  sieveFilterFormGroup: UntypedFormGroup;

  bisDatumPlatzhalter = "{BisDatum}";
  vornamePlatzhalter = "{Vorname}";
  nachnamePlatzhalter = "{Nachname}";

  get today(): Date {
    return new Date();
  }

  get tomorrow(): Date {
    const date = new Date();
    date.setDate(this.today.getDate() + 1);
    return date;
  }

  get minDate(): string {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  }

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
    private mailmanagerService: MailmanagerService,
    private fb: FormBuilder,
    private loveItToastService: LoveItToastService,
    private loveItAlertService: LoveItAlertService,
  ) {}

  ngOnInit() {
    this.sieveFilterFormGroup = this.fb.group(
      {
        id: [null],
        message: [this.getDefaultMessage(), [Validators.required]],
        from: [this.today.toISOString().split("T")[0]],
        to: [this.tomorrow.toISOString().split("T")[0]],
        vertretungId: [null],
        anmerkung: [null],
        notifyBackoffice: [true],
      },
      { validators: [this.dateRangeValidator()] },
    );

    if (this.existingSieveFilter) {
      this.sieveFilterFormGroup.patchValue({
        id: this.existingSieveFilter?.id,
        title: this.existingSieveFilter?.title,
        subject: this.existingSieveFilter?.subject,
        message: this.existingSieveFilter?.message,
        from: new Date(this.existingSieveFilter["is-active"]?.from).toISOString().split("T")[0],
        to: new Date(this.existingSieveFilter["is-active"]?.to).toISOString().split("T")[0],
        notifyBackoffice: this.existingSieveFilter?.saved_sieve_filter?.notify_backoffice,
        vertretungId: this.existingSieveFilter?.saved_sieve_filter?.vertretung_email,
        anmerkung: this.existingSieveFilter?.saved_sieve_filter?.anmerkung_email,
      });
    }
  }

  createSieveFilter() {
    return new Promise<void>((resolve, reject) => {
      this.mailmanagerService
        .mailmanagerSieveCreateCreate({
          token: this.oidcSecurityService.getToken(),
          user_id: this.userId,
          sieve_filter: this.getCreateUpdateSieveFilterParams(),
          abwesenheit: this.getCreateAbwesenheitParams(),
          notify_backoffice: this.sieveFilterFormGroup?.get("notifyBackoffice")?.value,
        } as MailmanagerCreateSieveRequest)
        .subscribe({
          next: (response) => {
            resolve();
          },
          error: (error) => {
            reject();
          },
        });
    });
  }

  updateSieveFilter() {
    return new Promise<void>((resolve, reject) => {
      this.mailmanagerService
        .mailmanagerSieveUpdateCreate({
          token: this.oidcSecurityService.getToken(),
          user_id: this.userId,
          filter_id: this.existingSieveFilter.id,
          sieve_filter: this.getCreateUpdateSieveFilterParams(),
          abwesenheit: this.getCreateAbwesenheitParams(),
        } as MailmanagerUpdateSieveRequest)
        .subscribe({
          next: (response) => {
            resolve();
          },
          error: (error) => {
            reject();
          },
        });
    });
  }

  getCreateUpdateSieveFilterParams() {
    const message = this.sieveFilterFormGroup.get("message").value;
    const replacedMessage = this.replacePlatzhalterForString(message);
    return {
      id: this.sieveFilterFormGroup.get("id").value,
      title: this.getDefaultTitle(this.today, this.tomorrow),
      subject: "Abwesenheit",
      message: replacedMessage,
      saved_sieve_filter: {
        sieve_id: this.existingSieveFilter?.id,
        notify_backoffice: this.sieveFilterFormGroup.get("notifyBackoffice")?.value,
        vertretung_email: this.sieveFilterFormGroup.get("vertretungId")?.value,
        anmerkung_email: this.sieveFilterFormGroup.get("anmerkung")?.value,
      },
      is_active: {
        from_date: this.sieveFilterFormGroup.get("from").value,
        to_date: this.sieveFilterFormGroup.get("to").value,
      },
    } as MailmanagerSieveFilter;
  }

  getCreateAbwesenheitParams() {
    return {
      SachbearbeiterId: this.userId,
      VertretungId: this.sieveFilterFormGroup.get("vertretungId").value,
      Anmerkung: this.sieveFilterFormGroup.get("anmerkung").value,
      Typ: AbwesenheitTyp.Urlaub,
      Von: new Date(this.sieveFilterFormGroup.get("from").value).toISOString(),
      Bis: new Date(this.sieveFilterFormGroup.get("to").value).toISOString(),
    } as Abwesenheit;
  }

  cancelButton() {
    this.modalController.dismiss(null, "cancel");
  }

  getDefaultMessage() {
    return (
      "Sehr geehrte Damen und Herren!\n" +
      " \n" +
      `Ich bin bis einschließlich ${this.bisDatumPlatzhalter} nicht erreichbar. Ihre E-Mail wird danach umgehend bearbeitet.\n` +
      "In dringenden Fällen wenden Sie sich bitte an office@faircheck.at oder 0316 890 685-0.\n" +
      " \n" +
      "Mit freundlichen Grüßen\n" +
      "\n" +
      `${this.vornamePlatzhalter} ${this.nachnamePlatzhalter}\n` +
      "\n" +
      "faircheck Schadenservice GmbH\n" +
      "8046 Graz-Stattegg, Dorfplatz 4\n" +
      "Telefon: 0316 890 685 0\n" +
      "Fax: 0316 890 685 10\n" +
      "office@faircheck.at, www.faircheck.at"
    );
  }

  getDefaultTitle(fromDate: Date, toDate: Date) {
    return `${fromDate.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })} - ${toDate.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })}`;
  }

  replacePlatzhalterForString(text: string): string {
    const toDate = this.sieveFilterFormGroup.get("to").value;

    if (toDate) {
      text = text.replace(
        new RegExp(this.bisDatumPlatzhalter, "g"),
        `${new Date(toDate).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })}`,
      );
    }

    text = text.replace(new RegExp(this.vornamePlatzhalter, "g"), `${this.userData?.given_name ?? ""}`);

    text = text.replace(new RegExp(this.nachnamePlatzhalter, "g"), `${this.userData?.family_name ?? ""}`);
    return text;
  }

  async showMissingVertretungOrAnmerkungAlert() {
    const alert = await this.loveItAlertService.create({
      header: "Nicht alle Felder ausgefüllt",
      message: `${
        this.sieveFilterFormGroup.get("vertretungId")?.value ? "" : "Es wurde keine Vertretung eingetragen. "
      }${this.sieveFilterFormGroup.get("anmerkung")?.value ? "" : "Es wurde keine Nachricht an BO eingetragen."}`,
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          text: "Trotzdem senden",
          role: "confirm",
          cssClass: "color-secondary",
        },
      ],
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    return role;
  }

  async submit() {
    this.submitted = true;
    if (this.sieveFilterFormGroup.invalid) {
      this.sieveFilterFormGroup.markAllAsTouched();
      return;
    }
    this.isSaving = true;

    let currentPromise: Promise<void> = null;
    if (this.entryExists()) {
      currentPromise = this.updateSieveFilter();
    } else {
      if (
        this.sieveFilterFormGroup.get("notifyBackoffice")?.value &&
        (!this.sieveFilterFormGroup.get("vertretungId")?.value || !this.sieveFilterFormGroup.get("anmerkung")?.value)
      ) {
        const missingFieldsAlertRole = await this.showMissingVertretungOrAnmerkungAlert();
        if (missingFieldsAlertRole === "confirm") {
          currentPromise = this.createSieveFilter();
        } else {
          this.isSaving = false;
          return;
        }
      } else {
        currentPromise = this.createSieveFilter();
      }
    }

    currentPromise
      .then(() => {
        this.isSaving = false;
        this.modalController.dismiss(null, "confirm");
      })
      .catch(async (error: Error) => {
        this.isSaving = false;
        this.loveItToastService.showToastMessage(error.message, ToastType.Error);
      });
  }

  dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fromCtrl = group.get("from");
      const toCtrl = group.get("to");
      const from = fromCtrl?.value;
      const to = toCtrl?.value;

      const minDate = new Date(this.minDate);
      minDate.setHours(0, 0, 0, 0);
      let hasError = false;

      if (from) {
        const fromDate = new Date(from);
        fromDate.setHours(0, 0, 0, 0);

        if (fromDate < minDate) {
          fromCtrl.setErrors({ customDateError: "Von-Datum darf maximal 1 Woche in der Vergangenheit liegen!" });
          hasError = true;
        }
      }

      if (to) {
        const toDate = new Date(to);
        toDate.setHours(0, 0, 0, 0);

        if (toDate < minDate) {
          toCtrl.setErrors({ customDateError: "Bis-Datum darf maximal 1 Woche in der Vergangenheit liegen!" });
          hasError = true;
        }
      }

      if (!hasError && from && to) {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);

        if (fromDate > toDate) {
          fromCtrl.setErrors({ customDateError: "Von-Datum darf nicht nach dem Bis-Datum liegen!" });
          hasError = true;
        }
      }

      if (!hasError && fromCtrl?.hasError("customDateError")) {
        fromCtrl.setErrors(null);
      }
      if (!hasError && toCtrl?.hasError("customDateError")) {
        toCtrl.setErrors(null);
      }
      return hasError ? { dateRange: true } : null;
    };
  }

  entryExists(): boolean {
    return this.existingSieveFilter && this.existingSieveFilter?.id === this.sieveFilterFormGroup.get("id").value;
  }
}
